<template>
  <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
    <h4 class="text-base font-medium text-gray-700 mb-3">Comments</h4>
    
    
    <!-- Loading state -->
    <div v-if="loading && !comments.length" class="flex justify-center items-center py-6">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
    </div>
    
    <!-- Error state -->
    <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
      <strong class="font-bold">Error:</strong>
      <span class="block sm:inline">{{ error.message }}</span>
      <button
      @click="retryFetchComments"
      class="mt-2 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
      Retry
    </button>
  </div>
  
  <!-- Empty state -->
  <div v-if="!loading && !comments.length" class="text-center py-6 text-gray-500">
    <p>No comments yet. Be the first to comment!</p>
  </div>
  
  <!-- Comments list -->
  <div v-else class="space-y-4">
    <CommentItem
    v-for="comment in comments"
    :key="comment.id"
    :comment="comment"
    :isLoading="loading"
    @edit="handleEdit"
    @delete="confirmDeleteComment"
    />
  </div>
  
  <!-- Add new comment form -->
  <div class="mb-6 bg-white rounded-lg shadow p-4">
    <div v-if="authError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
      <strong class="font-bold">Authentication Error:</strong>
      <span class="block sm:inline">{{ authError }}</span>
      <button
        @click="refreshAuth"
        class="mt-2 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
        Refresh Authentication
      </button>
    </div>
    
    <div class="flex gap-2">
      <textarea
        v-model="newComment"
        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-y min-h-[38px]"
        placeholder="Add a comment..."
        rows="1"
      ></textarea>
      <button
        @click="addComment"
        class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors h-[38px] whitespace-nowrap"
        :disabled="loading || !newComment.trim()"
      >
        <span v-if="loading">Sending...</span>
        <span v-else>Send</span>
      </button>
    </div>
  </div>
    
    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6">
        <h3 class="text-lg font-medium mb-3 dark:text-white">Confirm Delete</h3>
        <p class="text-gray-600 dark:text-gray-300 mb-6">
          Are you sure you want to delete this comment? This action cannot be undone.
        </p>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="showDeleteModal = false"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="deleteSelectedComment"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            :disabled="loading"
          >
            <span v-if="loading">Deleting...</span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useComments } from '@/composables/useComments';
import { IComment, ICommentUpdate } from '@/types/comment';
import { useAuth } from '@/composables/useAuth';
import CommentItem from './CommentItem.vue';

export default defineComponent({
  name: 'CommentList',
  props: {
    referenceId: {
      type: String,
      required: true
    }
  },
  components: {
    CommentItem
  },
  setup(props) {
    const {
      comments,
      loading,
      error,
      fetchComments,
      createComment,
      updateComment,
      deleteComment
    } = useComments();
    
    const { user, checkAndRefreshAuth } = useAuth();
    const newComment = ref('');
    const activeDropdown = ref<string | null>(null);
    const editingComment = ref<IComment | null>(null);
    const showDeleteModal = ref(false);
    const commentToDelete = ref<string | null>(null);
    const authError = ref<string | null>(null);
    
    // Load comments when component is mounted
    onMounted(async () => {
      try {
        // Check if user is authenticated
        if (!user.value) {
          console.log('User not authenticated on mount, trying to refresh auth');
          await checkAndRefreshAuth();
        }
        
        // Clear auth error if user is authenticated
        if (user.value?.id) {
          authError.value = null;
        }
        
        await fetchComments(props.referenceId);
      } catch (err) {
        console.error('Error loading comments:', err);
        // Error is already set in the fetchComments function
      }
    });
    
    // Retry fetching comments
    async function retryFetchComments() {
      try {
        error.value = null;
        await fetchComments(props.referenceId);
      } catch (err) {
        console.error('Error retrying to fetch comments:', err);
        // Error is already set in the fetchComments function
      }
    }
    
    // Refresh authentication
    async function refreshAuth() {
      try {
        authError.value = null;
        const authResult = await checkAndRefreshAuth();
        
        if (!authResult) {
          authError.value = "Failed to refresh authentication. Please try logging in again.";
        }
      } catch (err) {
        console.error('Error refreshing authentication:', err);
        authError.value = "Error refreshing authentication: " + (err instanceof Error ? err.message : String(err));
      }
    }
    
    // Format date to a readable format
    function formatDate(dateString: string): string {
      const date = new Date(dateString);
      
      // If it's today, show time only
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // Otherwise show date and time
      return date.toLocaleDateString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Toggle dropdown menu for a comment
    function toggleDropdown(commentId: string) {
      if (activeDropdown.value === commentId) {
        activeDropdown.value = null;
      } else {
        activeDropdown.value = commentId;
      }
    }
    
    // Add a new comment
    async function addComment() {
      if (!newComment.value.trim()) {
        console.error('Comment content is empty');
        return;
      }
      
      // Clear auth error if user is authenticated
      if (user.value?.id) {
        authError.value = null;
      }
      
      // Check if user is authenticated
      if (!user.value) {
        console.error('User is not authenticated');
        authError.value = "You must be logged in to post comments.";
        
        // Try to refresh authentication
        try {
          const authResult = await checkAndRefreshAuth();
          console.log('Auth refresh result:', authResult);
          
          if (!authResult || !user.value) {
            console.error('Still not authenticated after refresh');
            authError.value = "Authentication failed. Please try logging in again.";
            return;
          }
        } catch (authErr) {
          console.error('Error refreshing authentication:', authErr);
          authError.value = "Error refreshing authentication: " + (authErr instanceof Error ? authErr.message : String(authErr));
          return;
        }
      }
      
      // Double-check user is authenticated
      if (!user.value?.id) {
        console.error('User ID is still not available');
        authError.value = "User ID is not available. Please try logging in again.";
        return;
      }
      
      // Clear auth error if we got this far
      authError.value = null;
      
      try {
        console.log('Adding new comment:', {
          content: newComment.value.trim(),
          reference_id: props.referenceId,
          created_by: user.value.id
        });
        
        const result = await createComment({
          content: newComment.value.trim(),
          reference_id: props.referenceId,
          created_by: user.value.id
        });
        
        console.log('Comment added result:', result);
        
        // Clear the input and any error messages
        newComment.value = '';
        authError.value = null;
      } catch (err) {
        console.error('Error adding comment:', err);
      }
    }
    
    // Edit a comment
    function editComment(comment: IComment) {
      editingComment.value = { ...comment };
      activeDropdown.value = null;
    }
    
    // Cancel editing
    function cancelEdit() {
      editingComment.value = null;
    }
    
    // Save edited comment
    async function saveEdit() {
      if (!editingComment.value) return;
      
      try {
        await updateComment({
          id: editingComment.value.id,
          content: editingComment.value.content
        });
        
        // Exit edit mode
        editingComment.value = null;
      } catch (err) {
        console.error('Error updating comment:', err);
      }
    }
    
    // Handle edit from CommentItem
    async function handleEdit(commentUpdate: ICommentUpdate) {
      try {
        await updateComment(commentUpdate);
      } catch (err) {
        console.error('Error updating comment:', err);
      }
    }
    
    // Confirm delete comment
    function confirmDeleteComment(commentId: string) {
      commentToDelete.value = commentId;
      showDeleteModal.value = true;
      activeDropdown.value = null;
    }
    
    // Delete the selected comment
    async function deleteSelectedComment() {
      if (!commentToDelete.value) return;
      
      try {
        await deleteComment(commentToDelete.value);
        
        // Close the modal
        showDeleteModal.value = false;
        commentToDelete.value = null;
      } catch (err) {
        console.error('Error deleting comment:', err);
      }
    }
    
    return {
      comments,
      loading,
      error,
      authError,
      newComment,
      activeDropdown,
      editingComment,
      showDeleteModal,
      formatDate,
      toggleDropdown,
      addComment,
      editComment,
      cancelEdit,
      saveEdit,
      handleEdit,
      confirmDeleteComment,
      deleteSelectedComment,
      retryFetchComments,
      refreshAuth
    };
  }
});
</script>
